import React from 'react';
import glamorous from 'glamorous';
import PropTypes from 'prop-types';
import {WHITE, CHARCOAL, LIGHT_SHADED_GREY} from '../../../shared/style/colors';
import {PHONE, DESKTOP, LAPTOP} from '../../../shared/style/breakpoints';
import SimpleButton from '../../../shared/library/buttons/base/simple';
import {ALPHA} from '../../../shared/style/color-utils';
import CompaniesUsingStackshare from '../../../shared/library/companies-using-stackshare';
import {FONTS, WEIGHT} from '../../../shared/style/typography';
import StackshareImprovements from '../../../shared/library/stackshare-improvements';
import {MAX_PAGE_WIDTH} from '../../../shared/style/dimensions';

const Container = glamorous.div({
  display: 'block',
  marginBottom: 130,
  background: 'rgba(5,143,250, 0.03)',
  padding: '0 20px',
  [DESKTOP]: {
    padding: 0,
    margin: 'auto'
  },
  [LAPTOP]: {
    marginBottom: 96
  },
  [PHONE]: {
    padding: '0 20px',
    paddingTop: 30,
    margin: '30px auto 61px'
  }
});

const ExploreEnterprise = glamorous(SimpleButton)({
  minHeight: 56,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  background: CHARCOAL,
  border: 'none',
  borderRadius: 4,
  letterSpacing: 'normal',
  fontSize: 19,
  fontWeight: WEIGHT.ULTRA_BOLD,
  [DESKTOP]: {
    width: 446,
    height: 60
  },
  [LAPTOP]: {
    width: 446,
    padding: 0
  },
  '&:hover': {
    backgroundColor: ALPHA(CHARCOAL, 0.9),
    border: `1px solid ${CHARCOAL}`
  },
  ':focus': {
    color: WHITE
  }
}).withComponent('a');

const ButtonContainer = glamorous.div({
  display: 'flex',
  justifyContent: 'center',
  maxWidth: MAX_PAGE_WIDTH,
  margin: '54px auto',
  paddingBottom: 100,
  [PHONE]: {
    marginBottom: 0,
    paddingBottom: 68
  }
});

const ContentContainer = glamorous.div({
  border: `1px solid ${LIGHT_SHADED_GREY}`,
  margin: 'auto',
  marginBottom: 60,
  borderRadius: 5,
  display: 'block',
  background: WHITE,
  boxShadow: `0px 1px 10px 3px ${LIGHT_SHADED_GREY}`,
  [DESKTOP]: {
    display: 'flex',
    maxWidth: 1093
  },
  [PHONE]: {
    marginBottom: 80
  }
});

const ContentSubContainer = glamorous.div({
  margin: '46px 0 31px 53px',
  [DESKTOP]: {
    width: 767
  },
  [LAPTOP]: {
    marginBottom: 30,
    marginRight: 76
  },
  [PHONE]: {
    marginLeft: 35,
    marginBottom: 25,
    marginRight: 0
  }
});

const ContentImageText = glamorous.div({
  display: 'flex',
  [PHONE]: {
    display: 'block',
    paddingRight: 17
  }
});

const ContextText = glamorous.span({
  marginLeft: 38,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  fontWeight: WEIGHT.NORMAL,
  fontSize: 19,
  lineHeight: '38px',
  [PHONE]: {
    fontSize: 18,
    lineHeight: '35px',
    marginLeft: 0
  }
});

const CompanyNameContainer = glamorous.div({
  display: 'flex',
  marginTop: 10,
  [PHONE]: {
    marginLeft: 0,
    marginBottom: 20
  }
});

const CompanyNameText = glamorous.p({
  fontSize: 16,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  fontWeight: WEIGHT.NORMAL,
  lineHeight: '38px',
  textAlign: 'left',
  margin: '10px 10px 10px 19px',
  [PHONE]: {
    lineHeight: '25px'
  }
});

const ContentInsightsContainer = glamorous.div({
  marginLeft: 33,
  marginTop: 9,
  alignSelf: 'center',
  [LAPTOP]: {
    margin: 'auto',
    marginLeft: 78,
    marginBottom: 20
  },
  [PHONE]: {
    margin: 'auto',
    marginLeft: 35,
    marginBottom: 20
  },
  '> img': {
    width: 46,
    height: 46
  }
});

const ContentInsightsText = glamorous.p({
  fontSize: 18,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  fontWeight: WEIGHT.ULTRA_BLACK,
  lineHeight: '38px',
  color: CHARCOAL,
  textAlign: 'left',
  margin: 'auto'
});

const VerticalLineImage = glamorous.img({
  marginLeft: 35,
  marginTop: 56,
  height: 190,
  [LAPTOP]: {
    display: 'none'
  }
});

const HorizontalLineImage = glamorous.img({
  [PHONE]: {
    width: '90%'
  },
  [DESKTOP]: {
    display: 'none'
  }
});

const QuoteIconStyled = glamorous.img({
  width: 35,
  height: 35,
  [PHONE]: {
    display: 'block',
    marginBottom: 15
  }
});

const QuoteEndIconStyled = glamorous.img({
  display: 'block',
  width: 35,
  height: 35,
  alignSelf: 'end',
  marginLeft: 'auto',
  [PHONE]: {
    marginRight: 20
  }
});

const NameQuoteContainer = glamorous.div({
  display: 'flex',
  justifyContent: 'space-between',
  [PHONE]: {
    display: 'block',
    marginBottom: 15
  }
});

const verticalLine = 'https://img.stackshare.io/fe/landing/vertical_line.svg';
const horizontalLine = 'https://img.stackshare.io/fe/landing/horizontal_line.svg';
const quoteIcon = 'https://img.stackshare.io/fe/landing/quote-start.svg';
const quoteEndIcon = 'https://img.stackshare.io/fe/landing/quote-end.svg';
const insightsIcon = 'https://img.stackshare.io/fe/landing/tech_debt_new.svg';
const authorImage = 'https://img.stackshare.io/fe/landing/author_image1.svg';

const CompaniesUseStackshare = ({sendAnalyticsEvent}) => {
  return (
    <Container background={WHITE}>
      <CompaniesUsingStackshare
        title="Over 1,000 companies use Tech Stack Intelligence today"
        sendAnalyticsEvent={sendAnalyticsEvent}
      />
      <ContentContainer>
        <ContentSubContainer>
          <ContentImageText>
            <QuoteIconStyled src={quoteIcon} />
            <ContextText>
              I use StackShare <i>a lot</i>. I check in regularly to monitor our outstanding tech
              debt across the company, and alerts on Slack keep me updated on stack changes. We’ve
              saved hundreds of thousands of dollars so far by moving people to a standard set of
              technologies, with the potential to eventually save millions.
            </ContextText>
          </ContentImageText>
          <QuoteEndIconStyled src={quoteEndIcon} />
          <NameQuoteContainer>
            <CompanyNameContainer>
              <img src={authorImage} />
              <CompanyNameText>Jeff Green, CTO, Tyler Technologies</CompanyNameText>
            </CompanyNameContainer>
          </NameQuoteContainer>
          <HorizontalLineImage src={horizontalLine} />
        </ContentSubContainer>
        <VerticalLineImage src={verticalLine} />

        <ContentInsightsContainer>
          <img src={insightsIcon} />
          <ContentInsightsText>Shrink Tech Debt</ContentInsightsText>
        </ContentInsightsContainer>
      </ContentContainer>
      <StackshareImprovements customStyle={{position: 'relative'}} />
      <ButtonContainer>
        <ExploreEnterprise href={`/enterprise`}>Explore StackShare Enterprise</ExploreEnterprise>
      </ButtonContainer>
    </Container>
  );
};

CompaniesUseStackshare.propTypes = {
  sendAnalyticsEvent: PropTypes.func
};

export default CompaniesUseStackshare;
